import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Caixa de seleção padrão dos filtros de Pedidos. Antes cada filtro tinha um
// desenho: alguns eram botão com contador, o de caixa era um `Select` largo com
// outra altura. Agora todos têm o mesmo gatilho (ícone, rótulo, resumo do que
// está escolhido e seta) e a mesma lista, com busca quando é longa.
const LIMITE_PARA_BUSCA = 8;

export default function FiltroMulti({
  icone: Icone, rotulo, opcoes, selecionados = [], onToggle, onLimpar,
  unico = false, largura = "w-60", e2e,
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return opcoes;
    return opcoes.filter((o) => String(o.label).toLowerCase().includes(termo));
  }, [opcoes, busca]);

  const ativo = selecionados.length > 0;
  // Um item escolhido mostra o nome dele; vários mostram a contagem. Saber o
  // QUE está filtrado sem abrir o menu é metade do problema que isso resolve.
  const resumo = !ativo
    ? null
    : selecionados.length === 1
      ? (opcoes.find((o) => o.value === selecionados[0])?.label || selecionados[0])
      : `${selecionados.length} selecionados`;

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-e2e={e2e}
          className={`h-9 px-2.5 rounded-lg border text-sm flex items-center gap-1.5 max-w-[220px] transition-colors ${
            ativo
              ? "border-primary/50 bg-primary/5 text-foreground font-medium"
              : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          {Icone ? <Icone className="w-3.5 h-3.5 shrink-0" /> : null}
          <span className="truncate">{resumo || rotulo}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={`${largura} p-0`} align="start">
        {opcoes.length > LIMITE_PARA_BUSCA ? (
          <div className="relative border-b">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={`Buscar ${rotulo.toLowerCase()}...`}
              className="w-full h-9 pl-8 pr-2 text-sm bg-transparent outline-none"
            />
          </div>
        ) : null}
        <div className="max-h-64 overflow-y-auto py-1">
          {filtradas.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground text-center">Nada encontrado.</p>
          ) : null}
          {filtradas.map((opcao) => {
            const marcado = selecionados.includes(opcao.value);
            return (
              <button
                key={opcao.value}
                type="button"
                onClick={() => {
                  onToggle(opcao.value, !marcado);
                  if (unico) setAberto(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors"
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                  marcado ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"
                }`}>
                  {marcado ? <Check className="w-3 h-3" strokeWidth={3} /> : null}
                </span>
                {opcao.icone ? <opcao.icone className="w-3.5 h-3.5 shrink-0 text-muted-foreground" /> : null}
                <span className="text-sm truncate flex-1">{opcao.label}</span>
                {opcao.detalhe ? (
                  <span className="text-[11px] text-muted-foreground tabular-nums">{opcao.detalhe}</span>
                ) : null}
              </button>
            );
          })}
        </div>
        {ativo && onLimpar ? (
          <button
            type="button"
            onClick={() => { onLimpar(); setAberto(false); }}
            className="w-full border-t py-2 text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            Limpar seleção
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
